import { BenefitTypeOptions, USStateOptions } from '@strata/constants';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DialogWithForm } from '@strata/react-dialog';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { IssueDetail } from '@strata/react-status-icons';
import { ErrorToast, SuccessToast, WarningToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { fixInvalidReferralOpportunity } from 'api/opportunityCuratorAPI';
import { handleError } from 'services/Error';
import { useGetOpportunityQuery } from 'store/api/opportunityCuratorApi';
import {
    useGetPlacesOfServiceQuery,
    useLazyGetPrescriberDetailsQuery,
} from 'store/api/referenceApi';
import { resetAllPages } from 'store/opportunities/opportunitiesSlice';

export const FixInvalidDataDialog = ({
    isOpen,
    onClose,
    opportunityDetails = undefined,
    memberDetails = undefined,
    backToDestination,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { data: placesOfService, isFetching: placesOfServiceFetching } =
        useGetPlacesOfServiceQuery();
    const { referralId } = opportunityDetails ?? {};
    const { refetch: refetchOpportunity } = useGetOpportunityQuery(referralId, {
        skip: !referralId,
    });

    const [getPrescriberDetails] = useLazyGetPrescriberDetailsQuery();

    const packageOpportunityFixPayload = async (values) => {
        const issues = values?.issues ?? [];
        const fixParams = { referralId };
        if (
            issues.includes(IssueDetail.IssueTypes.INVALID_PRESCRIBER_NPI) &&
            values.prescriberNpi
        ) {
            const { isError } = await getPrescriberDetails({ prescriberNpi: values.prescriberNpi });
            if (isError) {
                ErrorToast({ message: 'Prescriber does not exist' });
                return {};
            }
            fixParams.prescriberNpi = values.prescriberNpi;
        }
        if (issues.includes(IssueDetail.IssueTypes.INVALID_DAYS_SUPPLY)) {
            fixParams.benefit = values.benefit;
        }
        if (issues.includes(IssueDetail.IssueTypes.MISSING_PLACE_OF_SERVICE)) {
            fixParams.placeOfService = values.placeOfService;
        }
        if (issues.includes(IssueDetail.IssueTypes.INVALID_MEMBER_ADDRESS)) {
            fixParams.memberAddress1 = values.memberAddress1;
            fixParams.memberCity = values.memberCity;
            fixParams.memberState = values.memberState;
            fixParams.memberZip = values.memberZip;
        }
        if (issues.includes(IssueDetail.IssueTypes.WAIT_FOR_CLAIM)) {
            if (values.waitForClaim != null) {
                fixParams.waitForClaim = !values.waitForClaim;
            }
        }
        return fixParams;
    };

    return (
        <Formik
            initialValues={{
                issues: opportunityDetails?.issues,
                prescriberNpi: opportunityDetails?.prescriberNpi ?? undefined,
                placeOfService: opportunityDetails?.placeOfService ?? undefined,
                benefit: opportunityDetails?.benefit ?? undefined,
                memberAddress1: memberDetails?.memberAddress1 ?? undefined,
                memberCity: memberDetails?.memberCity ?? undefined,
                memberState: memberDetails?.memberState ?? undefined,
                memberZip: memberDetails?.memberZip ?? undefined,
            }}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                prescriberNpi: Yup.string(),
                placeOfService: Yup.string(),
                benefit: Yup.string(),
                memberAddress1: Yup.string(),
                memberCity: Yup.string(),
                memberState: Yup.string().length(2, 'Must be exactly 2 characters.'),
                memberZip: Yup.string()
                    .min(5, 'Must be at least 5 characters.')
                    .max(10, validationStrings.maxCharacter[10]),
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    const fixParams = await packageOpportunityFixPayload(values);
                    if (Object.keys(fixParams).length === 0) {
                        return;
                    }

                    const response = await fixInvalidReferralOpportunity(fixParams);

                    const { issues: remainingIssues } = response;
                    const numRemaining = Object.keys(remainingIssues).length;
                    if (numRemaining === 0) {
                        SuccessToast({ message: 'Opportunity data updated successfully.' });
                        // Navigate back to previous page. Opportunity page may no longer exist.
                        // Do not pass backToDestination state, allow destination to decide.
                        navigate(backToDestination.route);
                    } else {
                        WarningToast({
                            message: `Opportunity updated. ${numRemaining} issues remain.`,
                        });
                        refetchOpportunity();
                    }
                    dispatch(resetAllPages());
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to update opportunity data. Please try again.', err);
                }
            }}
        >
            {(formik) => {
                const issues = formik.values?.issues ?? [];

                return (
                    <DialogWithForm
                        dialogTitle="Fix Invalid Data"
                        width={560}
                        isOpen={isOpen}
                        secondaryButtonLabel="Cancel"
                        onSecondaryButtonClick={() => {
                            onClose();
                            formik.resetForm();
                        }}
                        primaryButtonLabel="Submit Referral"
                        isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                        dialogContent={
                            <>
                                <div className="margin__bottom--x-small">
                                    This action will put the opportunity through validation and
                                    eligibility checks.
                                </div>
                                <div className="margin__bottom--medium">
                                    When all issues have been resolved the opportunity will be
                                    routed appropriately.
                                </div>
                                <>
                                    {issues.includes(
                                        IssueDetail.IssueTypes.INVALID_PRESCRIBER_NPI,
                                    ) && (
                                        <TextInputFormik
                                            id="prescriberNpi"
                                            label="Prescriber NPI"
                                        />
                                    )}
                                    {issues.includes(
                                        IssueDetail.IssueTypes.INVALID_DAYS_SUPPLY,
                                    ) && (
                                        <SuggestSingleSelectFormik
                                            id="benefit"
                                            label="Benefit"
                                            values={BenefitTypeOptions}
                                            onChange={(newValue) =>
                                                formik.setFieldValue('benefit', newValue?.value)
                                            }
                                            selectedItem={BenefitTypeOptions.find(
                                                ({ value }) => value === formik.values.benefit,
                                            )}
                                        />
                                    )}
                                    {issues.includes(
                                        IssueDetail.IssueTypes.MISSING_PLACE_OF_SERVICE,
                                    ) && (
                                        <SuggestSingleSelectFormik
                                            id="placeOfService"
                                            label="Place Of Service"
                                            isLoading={placesOfServiceFetching}
                                            values={placesOfService}
                                            onChange={(newValue) =>
                                                formik.setFieldValue(
                                                    'placeOfService',
                                                    newValue?.value,
                                                )
                                            }
                                            selectedItem={placesOfService?.find(
                                                ({ value }) =>
                                                    value === formik.values.placeOfService,
                                            )}
                                        />
                                    )}
                                    {issues.includes(
                                        IssueDetail.IssueTypes.INVALID_MEMBER_ADDRESS,
                                    ) && (
                                        <>
                                            <div className="sds_update-referral-data__section-title">
                                                Member Address
                                            </div>
                                            <TextInputFormik id="memberAddress1" label="Address" />
                                            <TextInputFormik id="memberCity" label="City" />
                                            <SuggestSingleSelectFormik
                                                id="memberState"
                                                label="State"
                                                values={USStateOptions}
                                                onChange={(newValue) =>
                                                    formik.setFieldValue(
                                                        'memberState',
                                                        newValue?.secondaryData,
                                                    )
                                                }
                                                selectedItem={USStateOptions.find(
                                                    ({ secondaryData }) =>
                                                        secondaryData === formik.values.memberState,
                                                )}
                                            />
                                            <TextInputFormik id="memberZip" label="ZIP Code" />
                                        </>
                                    )}
                                    {issues.includes(IssueDetail.IssueTypes.WAIT_FOR_CLAIM) && (
                                        <CheckboxListFormik
                                            name="waitForClaim"
                                            label="Wait for Claim"
                                            options={['Remove Wait For Claim']}
                                            values={
                                                formik.values.waitForClaim
                                                    ? ['Remove Wait For Claim']
                                                    : []
                                            }
                                            onChange={(newValue) =>
                                                formik.setFieldValue(
                                                    'waitForClaim',
                                                    newValue.includes('Remove Wait For Claim'),
                                                )
                                            }
                                        />
                                    )}
                                </>
                            </>
                        }
                    />
                );
            }}
        </Formik>
    );
};

FixInvalidDataDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    opportunityDetails: PropTypes.shape({
        referralId: PropTypes.string.isRequired,
        issues: PropTypes.arrayOf(PropTypes.string),
        prescriberNpi: PropTypes.string,
        placeOfService: PropTypes.string,
        benefit: PropTypes.string,
        isBrand: PropTypes.bool,
    }),
    memberDetails: PropTypes.shape({
        memberAddress1: PropTypes.string,
        memberCity: PropTypes.string,
        memberState: PropTypes.string,
        memberZip: PropTypes.string,
    }),
    backToDestination: PropTypes.shape({
        route: PropTypes.string,
        text: PropTypes.string,
    }).isRequired,
};
